/**
 * API Utility Module - Single Source of Truth for All API Calls
 *
 * This module provides authenticated fetch functionality for the entire app.
 * All API calls should use authFetch() to ensure proper authentication.
 *
 * Usage:
 *   import { authFetch, apiUrl } from '../utils/api';
 *   const response = await authFetch(apiUrl('/api/conversations'));
 *   const data = await response.json();
 */

import { apiUrl, API_BASE_URL, FRONTEND_URL } from "../config/api";

// Re-export for convenience
export { apiUrl, API_BASE_URL, FRONTEND_URL };

// Global token getter - set by AuthConnector in index.js
let globalGetToken = null;

/**
 * Set the global token getter function
 * Called once at app initialization from index.js
 * @param {Function} getTokenFn - Clerk's getToken function with skipCache
 */
export function setTokenGetter(getTokenFn) {
  globalGetToken = getTokenFn;
}

/**
 * Authenticated fetch - use this for ALL API calls that require auth
 *
 * Features:
 * - Automatically adds Authorization header with Clerk token
 * - Uses skipCache to always get fresh tokens
 * - Includes credentials for cookie fallback
 * - Returns raw Response object (caller handles .json())
 *
 * @param {string} url - Full URL to fetch
 * @param {object} options - Fetch options (method, body, headers, etc.)
 * @returns {Promise<Response>} - Fetch response
 */
export async function authFetch(url, options = {}) {
  // Get auth token if available
  let token = null;
  if (globalGetToken) {
    try {
      token = await globalGetToken();
    } catch (err) {
      // Silent fail - will make unauthenticated request
    }
  }

  // Build headers - merge with any provided headers
  const headers = {
    ...options.headers,
  };

  // Only set Content-Type if not FormData (browser sets boundary for multipart)
  if (!(options.body instanceof FormData)) {
    headers["Content-Type"] = "application/json";
  }

  // Add Authorization header if we have a token
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Member timezone, so the assistant quotes times in the member's zone
  // (server stores it in user_settings.timezone; ticket ce0d5864).
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (tz && !headers["X-Timezone"]) headers["X-Timezone"] = tz;
  } catch (_) { /* older browsers: server default applies */ }

  // Build fetch options
  const fetchOptions = {
    ...options,
    credentials: "include",
    headers,
  };

  // Make the request
  const response = await fetch(url, fetchOptions);

  // One retry with a FORCED fresh token — pairs with the 30s token cache in
  // index.js so a just-expired cached token never costs the user a call.
  if (response.status === 401 && globalGetToken) {
    try {
      const fresh = await globalGetToken({ force: true });
      if (fresh) {
        return await fetch(url, {
          ...fetchOptions,
          headers: { ...headers, Authorization: `Bearer ${fresh}` },
        });
      }
    } catch (_) { /* fall through with the original 401 */ }
  }
  return response;
}

/**
 * Authenticated fetch that parses JSON and throws on error
 * Use this when you want automatic error handling
 *
 * @param {string} url - Full URL to fetch
 * @param {object} options - Fetch options
 * @returns {Promise<any>} - Parsed JSON response
 * @throws {Error} - If response is not ok or JSON parsing fails
 */
export async function authFetchJson(url, options = {}) {
  const response = await authFetch(url, options);

  if (!response.ok) {
    const error = await response
      .json()
      .catch(() => ({ message: "Request failed" }));
    throw new Error(
      error.message || `HTTP ${response.status}: ${response.statusText}`,
    );
  }

  return response.json();
}

/**
 * Auth API calls
 */
export const authApi = {
  signup: async (email, password, name) => {
    return authFetchJson(apiUrl("/api/auth/signup"), {
      method: "POST",
      body: JSON.stringify({ email, password, name }),
    });
  },

  signin: async (email, password) => {
    return authFetchJson(apiUrl("/api/auth/signin"), {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
  },

  signout: async () => {
    return authFetchJson(apiUrl("/api/auth/signout"), {
      method: "POST",
    });
  },

  getSession: async () => {
    return authFetchJson(apiUrl("/api/auth/session"));
  },

  getMe: async () => {
    return authFetchJson(apiUrl("/api/auth/me"));
  },
};

/**
 * Affiliate API calls
 */
export const affiliateApi = {
  getDashboard: async (timeRange = "all") => {
    const url =
      timeRange && timeRange !== "all"
        ? apiUrl(`/api/affiliate/dashboard?timeRange=${timeRange}`)
        : apiUrl("/api/affiliate/dashboard");
    return authFetchJson(url);
  },

  getInfo: async () => {
    return authFetchJson(apiUrl("/api/affiliate/info"));
  },

  getInvites: async () => {
    return authFetchJson(apiUrl("/api/affiliate/invites"));
  },

  trackClick: async (ref) => {
    return authFetchJson(apiUrl(`/api/affiliate/track-click?ref=${ref}`));
  },

  getClickStats: async () => {
    return authFetchJson(apiUrl("/api/affiliate/stats/clicks"));
  },

  getConversionStats: async () => {
    return authFetchJson(apiUrl("/api/affiliate/stats/conversions"));
  },

  startStripeConnect: async () => {
    return authFetchJson(apiUrl("/api/affiliate/connect/start"), {
      method: "POST",
    });
  },

  joinProgram: async () => {
    return authFetchJson(apiUrl("/api/auth/create-affiliate"), {
      method: "POST",
    });
  },
};

/**
 * Sanitize an affiliate code for safe embedding in a URL.
 * Mirrors the server-side rule: alphanumeric + hyphen/underscore, max 64.
 */
function sanitizeAffiliateCode(affiliateCode) {
  return String(affiliateCode || "")
    .replace(/[^a-zA-Z0-9_-]/g, "")
    .slice(0, 64);
}

/**
 * Build affiliate referral link
 * Always points to activateoptimus.com — the official signup page.
 */
export function buildAffiliateLink(affiliateCode) {
  return `https://activateoptimus.com?ref=${affiliateCode}`;
}

/**
 * Build affiliate link to the free webinar walkthrough (best first touch).
 * The /os page persists ?ref= first-touch and forwards it to checkout.
 */
export function buildWalkthroughLink(affiliateCode) {
  return `https://optimus.university/os?ref=${sanitizeAffiliateCode(affiliateCode)}`;
}

/**
 * Build affiliate link to Optimus University (free resources portal).
 * Every portal page persists ?ref= first-touch; all paths lead to checkout.
 */
export function buildUniversityLink(affiliateCode) {
  return `https://optimus.university?ref=${sanitizeAffiliateCode(affiliateCode)}`;
}

/**
 * Copy text to clipboard
 */
export async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error("Failed to copy:", err);
    return false;
  }
}
